/**
 * Hook pour la gestion des factures
 * Utilise React Query pour le cache et les mutations
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invoicesApi, type InvoicesQueryParams } from '@/services/api';
import type { Invoice, InvoiceFormData } from '@/types';
import { localInputDate } from '@/lib/utils';
import { forecastKeys } from './useForecast';
import { useMutationWithToast } from './useMutationWithToast';
import { projectKeys } from './useProjects';
import { useToast } from '@/components/ui/toast';

// ========================================
// Query Keys
// ========================================

export const invoiceKeys = {
    all: ['invoices'] as const,
    lists: () => [...invoiceKeys.all, 'list'] as const,
    list: (params: InvoicesQueryParams) => [...invoiceKeys.lists(), params] as const,
    details: () => [...invoiceKeys.all, 'detail'] as const,
    detail: (id: string) => [...invoiceKeys.details(), id] as const,
};

// ========================================
// Hooks
// ========================================

/**
 * Liste paginée des factures
 */
export function useInvoices(params?: InvoicesQueryParams) {
    return useQuery({
        queryKey: invoiceKeys.list(params || {}),
        queryFn: () => invoicesApi.getAll(params),
        staleTime: 30_000, // 30 secondes
    });
}

/**
 * Détail d'une facture
 */
export function useInvoice(id: string) {
    return useQuery({
        queryKey: invoiceKeys.detail(id),
        queryFn: () => invoicesApi.getById(id),
        enabled: !!id,
    });
}

/**
 * Créer une facture
 */
export function useCreateInvoice() {
    return useMutationWithToast({
        mutationFn: (invoice: InvoiceFormData) => invoicesApi.create(invoice),
        operationName: 'Création facture',
        successMessage: 'Facture créée avec succès',
        invalidateKeys: [invoiceKeys.lists(), forecastKeys.all, projectKeys.all],
    });
}

/**
 * Modifier une facture
 */
export function useUpdateInvoice() {
    const queryClient = useQueryClient();

    return useMutationWithToast({
        mutationFn: ({ id, data }: { id: string; data: Partial<InvoiceFormData> }) =>
            invoicesApi.update(id, data),
        operationName: 'Modification facture',
        successMessage: 'Facture modifiée avec succès',
        onSuccess: (updatedInvoice: Invoice) => {
            queryClient.setQueryData(invoiceKeys.detail(updatedInvoice.id), updatedInvoice);
        },
        invalidateKeys: [invoiceKeys.lists(), forecastKeys.all, projectKeys.all],
    });
}

/**
 * Supprimer une facture
 */
export function useDeleteInvoice() {
    return useMutationWithToast({
        mutationFn: (id: string) => invoicesApi.delete(id),
        operationName: 'Suppression facture',
        successMessage: 'Facture supprimée',
        invalidateKeys: [invoiceKeys.lists(), forecastKeys.all, projectKeys.all],
    });
}

/**
 * Marquer une facture comme payée
 */
export function useMarkInvoicePaid() {
    const queryClient = useQueryClient();

    return useMutationWithToast({
        mutationFn: ({ id, paidDate }: { id: string; paidDate: string }) =>
            invoicesApi.markAsPaid(id, paidDate),
        operationName: 'Marquage facture payée',
        successMessage: 'Facture marquée comme payée',
        onSuccess: (updatedInvoice: Invoice) => {
            queryClient.setQueryData(invoiceKeys.detail(updatedInvoice.id), updatedInvoice);
        },
        invalidateKeys: [invoiceKeys.lists(), forecastKeys.all, projectKeys.all],
    });
}

/**
 * Annuler l'encaissement d'une facture : elle repasse émise et son écriture
 * disparaît du grand livre, donc le solde redescend du TTC.
 */
export function useUnmarkInvoicePaid() {
    const queryClient = useQueryClient();

    return useMutationWithToast({
        mutationFn: (id: string) => invoicesApi.unmarkPaid(id),
        operationName: 'Annulation de l\'encaissement',
        successMessage: 'Encaissement annulé, la facture repasse émise',
        onSuccess: (updatedInvoice: Invoice) => {
            queryClient.setQueryData(invoiceKeys.detail(updatedInvoice.id), updatedInvoice);
        },
        invalidateKeys: [invoiceKeys.lists(), forecastKeys.all, projectKeys.all],
    });
}

/**
 * Annuler une facture
 */
export function useCancelInvoice() {
    const queryClient = useQueryClient();

    return useMutationWithToast({
        mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
            invoicesApi.cancel(id, reason),
        operationName: 'Annulation facture',
        successMessage: 'Avoir d\'annulation émis',
        // La réponse est l'AVOIR, pas la facture d'origine (qui reste émise) : on
        // met en cache le détail de l'avoir et la liste se rafraîchit par invalidation.
        onSuccess: (creditNote: Invoice) => {
            queryClient.setQueryData(invoiceKeys.detail(creditNote.id), creditNote);
        },
        invalidateKeys: [invoiceKeys.lists(), forecastKeys.all, projectKeys.all],
    });
}

/**
 * Envoyer une facture par email
 * TODO: Route non disponible dans le backend actuel
 */
// export function useSendInvoice() {
//     return useMutation({
//         mutationFn: (id: string) => invoicesApi.send(id),
//         onError: (error) => {
//             console.error('[Invoices] Erreur envoi:', error);
//         },
//     });
// }

/**
 * Télécharger le PDF d'une facture
 * TODO: Route non disponible dans le backend actuel
 */
export function useDownloadInvoicePdf() {
    return useMutationWithToast({
        mutationFn: (id: string) => invoicesApi.downloadPdf(id),
        operationName: 'Téléchargement PDF',
        successMessage: 'PDF téléchargé',
        invalidateKeys: [],
    });
}

/**
 * Émettre une facture (DRAFT → EMITTED avec numérotation)
 *
 * Si l'org a une PaConnection ACTIVE, l'envoi vers la Plateforme Agréée est tenté
 * synchronement. Le résultat est dans `invoice.paResult`. On affiche un toast adapté :
 *  - envoi PA OK : toast success "Facture émise et transmise"
 *  - envoi PA échoué : toast warning "Facture émise, transmission échouée", à réessayer depuis la facture
 *  - plateforme raccordée, client sans SIRET : toast info, traité comme un particulier
 *  - aucune plateforme raccordée et client avec SIRET : toast warning avec lien vers le raccordement
 *  - aucune plateforme raccordée sinon : toast success simple "Facture émise"
 */
export function useEmitInvoice() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutationWithToast({
        mutationFn: (id: string) => invoicesApi.emit(id),
        operationName: 'Émission facture',
        // successMessage géré manuellement dans onSuccess pour différencier les cas PA
        onSuccess: (updatedInvoice: Invoice) => {
            queryClient.setQueryData(invoiceKeys.detail(updatedInvoice.id), updatedInvoice);
            const paResult = updatedInvoice.paResult;
            if (paResult?.skipped && paResult.buyerIsBusiness) {
                toast.addToast({
                    type: 'warning',
                    title: 'Facture émise, mais pas envoyée à une plateforme agréée',
                    message: `Numéro ${updatedInvoice.numero} attribué. Raccordez votre plateforme dans Paramètres pour la transmettre à votre client.`,
                    action: { label: 'Raccorder ma plateforme', to: '/settings?tab=facturation' },
                });
            } else if (paResult?.notApplicable) {
                toast.info(
                    'Facture émise',
                    `Numéro ${updatedInvoice.numero} attribué. Ce client n'a pas de SIRET : BTPal le considère comme un particulier et ne transmet rien. Si c'est une entreprise, ajoutez son SIRET dans sa fiche client, puis cliquez sur Transmettre dans la facture.`
                );
            } else if (!paResult || paResult.skipped) {
                toast.success('Facture émise', `Numéro ${updatedInvoice.numero} attribué.`);
            } else if (paResult.ok) {
                toast.success(
                    'Facture émise et transmise',
                    `Numéro ${updatedInvoice.numero}. Transmise à la plateforme agréée.`
                );
            } else {
                toast.warning(
                    'Facture émise, transmission échouée',
                    paResult.errorMessage
                        ? `${paResult.errorMessage} Vous pouvez réessayer depuis la facture.`
                        : 'Vous pouvez réessayer depuis la facture.'
                );
            }
        },
        invalidateKeys: [invoiceKeys.lists(), forecastKeys.all, projectKeys.all],
    });
}

/**
 * Réessaie l'envoi d'une facture vers la Plateforme Agréée. Un client toujours sans
 * SIRET n'est pas un échec : information, la facture reste « non concernée ».
 */
export function useRetryPaInvoice() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutationWithToast({
        mutationFn: async (id: string) => {
            const { paApi } = await import('@/services/api');
            return paApi.retryInvoice(id);
        },
        operationName: 'Réessai envoi PA',
        onSuccess: (result, id) => {
            if (result.ok && result.reconciled) {
                toast.success(
                    'Facture déjà transmise',
                    'La Plateforme Agréée l\'avait déjà reçue. Le suivi de son statut est rétabli.'
                );
                queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
                queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
            } else if (result.ok) {
                toast.success('Transmission PA réussie', 'La facture est maintenant chez la Plateforme Agréée.');
                queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
                queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
            } else if (result.notApplicable) {
                toast.info(
                    'Rien à transmettre',
                    "Ce client n'a toujours pas de SIRET dans sa fiche client. Si c'est un particulier, vous n'avez rien à faire. Sinon, ajoutez son SIRET, puis cliquez de nouveau sur Transmettre."
                );
                queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
                queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
            } else if (result.skipped) {
                toast.warning(
                    'Aucune Plateforme Agréée connectée',
                    'Activez la facturation électronique dans Paramètres > Facturation électronique.'
                );
            } else {
                toast.error(
                    'Échec de la transmission PA',
                    result.errorMessage ?? 'Erreur inconnue'
                );
                queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
                queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
            }
        },
    });
}

/**
 * Envoyer une facture par email
 */
export function useSendInvoiceByEmail() {
    return useMutationWithToast({
        mutationFn: ({ id, recipientEmail, message }: { id: string; recipientEmail: string; message?: string }) =>
            invoicesApi.sendByEmail(id, { recipientEmail, message }),
        operationName: 'Envoi facture par email',
        successMessage: 'Facture envoyée par email',
        invalidateKeys: [invoiceKeys.lists()],
    });
}

/**
 * Créer un avoir sur une facture
 */
export function useCreateCreditNote() {
    return useMutationWithToast({
        mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
            invoicesApi.createCreditNote(id, { reason }),
        operationName: 'Création avoir',
        successMessage: 'Avoir créé avec succès',
        invalidateKeys: [invoiceKeys.lists(), forecastKeys.all, projectKeys.all],
    });
}

/**
 * Exporter les factures au format CSV comptable
 */
export function useExportCompta() {
    return useMutationWithToast({
        mutationFn: async (params?: { startDate?: string; endDate?: string }) => {
            const response = await invoicesApi.exportCompta(params);
            const blob = new Blob(['\uFEFF' + (response.data as string)], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `export-compta-${localInputDate()}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            return response;
        },
        operationName: 'Export comptable',
        successMessage: 'Export téléchargé',
    });
}
